import { FormattedNumberInputField } from "@/components/shared";
import { postiveNumberZeroToOneHundred, stringToNumberOrUndefined } from "@/utils/form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RenderFormRowComponent from "./RenderFormRowComponent";

/**
 * Creates a zod schema for the contract form group
 * All fields are requirements are based off quickoffer tender type, public and private tenders validation handled in main schema
 * @param t - The translation function
 * @returns The zod schema
 */
export const createContractSchema = (t: TFunction) => {
  return z.object({
    contract: z.object({
      paymentTerm: z.preprocess(
        stringToNumberOrUndefined,
        z.number().int().positive().optional()
      ),
      escalationClause: z.preprocess(
        stringToNumberOrUndefined,
        // Duplicate of postiveNumberZeroToOneHundred due putting optional after preprocess does give us the desired results
        // And can't put optional in the function due to it being generic and used in other places
        z.number({
          error: (issue) => issue.input === undefined ?
            `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.isrequired")}`
            :
            `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.benumber")}`,
        })
          .min(0, `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.atleast0")}`)
          .max(100, `${t("newtender.formgroups.contract.inputs.escalationclause.label")} ${t("schemas:form.atmax100")}`).optional()
      ),
      priceCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.price.label")).optional(),
      qualityCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.qualityconcept.label")).optional(),
      referenceCriteria: postiveNumberZeroToOneHundred(t("newtender.formgroups.contract.inputs.references.label")).optional(),
    })
  });
};

/**
 * ContractFormGroup component for editing the contract form group
 * Utilises newtender locales as these values are based off the new tender form, if newtender changes, these will change with it.
 * @param form - The form object
 * @returns The rendered contract form group component
 */
export function ContractFormGroup({ form }: { form: UseFormReturn<any>}) {
  const { t } = useTranslation();

  return (
    <Card id="contract">
      <CardHeader className="pt-4 pb-2 select-none">
        <CardTitle className="text-lg">{t("newtender.formgroups.contract.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <div>
          {/* Payment Term */}
          <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.paymentterm.label")}>
            <FormattedNumberInputField
              control={form.control}
              name="contract.paymentTerm"
              placeholder={t("newtender.formgroups.contract.inputs.paymentterm.placeholder")}
              additionalSuffix={t("newtender.formgroups.contract.inputs.paymentterm.suffix")}
              isInteger
              spaceBetweenSuffix={true}
            />
            <p className="input-error-message mt-2">{form.formState.errors.contract?.["paymentTerm"]?.message}</p>
          </RenderFormRowComponent>

          <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.escalationclause.label")}>
            <FormattedNumberInputField
              control={form.control}
              name="contract.escalationClause"
              placeholder={t("newtender.formgroups.contract.inputs.escalationclause.placeholder")}
              additionalSuffix="%"
            />
            <p className="input-error-message mt-2">{form.formState.errors.contract?.["escalationClause"]?.message}</p>
          </RenderFormRowComponent>

          <div className="flex flex-col gap-2">
            <Card className="flex flex-col p-4 m-4">
              <div className="flex flex-col gap-2 px-4">
                <Label>{t("newtender.formgroups.contract.awardingcriteria")}</Label>
                <p className="text-xs text-slate-400">{t("newtender.formgroups.contract.awardingcriteriadesc")}</p>
              </div>
              <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.price.label")}>
                <FormattedNumberInputField
                  control={form.control}
                  name="contract.priceCriteria"
                  placeholder={t("newtender.formgroups.contract.inputs.price.placeholder")}
                  additionalSuffix="%"
                />
                <p className="input-error-message mt-2">{form.formState.errors.contract?.["priceCriteria"]?.message}</p>
              </RenderFormRowComponent>
              <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.qualityconcept.label")}>
                <FormattedNumberInputField
                  control={form.control}
                  name="contract.qualityCriteria"
                  placeholder={t("newtender.formgroups.contract.inputs.qualityconcept.placeholder")}
                  additionalSuffix="%"
                />
                <p className="input-error-message mt-2">{form.formState.errors.contract?.["qualityCriteria"]?.message}</p>
              </RenderFormRowComponent>
              <RenderFormRowComponent label={t("newtender.formgroups.contract.inputs.references.label")}>
                <FormattedNumberInputField
                  control={form.control}
                  name="contract.referenceCriteria"
                  placeholder={t("newtender.formgroups.contract.inputs.references.placeholder")}
                  additionalSuffix="%"
                />
                <p className="input-error-message mt-2">{form.formState.errors.contract?.["referenceCriteria"]?.message}</p>
              </RenderFormRowComponent>
              {
                form.formState.errors.contract?.root && (
                  <p className="input-error-message px-4 w-2/3 ml-auto">
                    {form.formState.errors.contract?.root.message as string}
                  </p>
                )
              }
            </Card>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
